/**
 * RuleSetAdminPage — CRUD management for RuleSet configs (admin/architect layer).
 *
 * Route: /ferp-core/rulesets/admin
 * Access: RequireScope("regulatory:import") — enforced at backend resolver level.
 *
 * Actions:
 * - Import new RuleSet (opens RuleSetImportModal)
 * - Edit metadata (display name, description, safety level)
 * - Supersede (publish new version — opens RuleSetSupersedeModal)
 * - Sunset (set an end date)
 *
 * DRR-055: Dumb frontend — renders backend data, no domain logic.
 * DRR-096: Temporal validity — shows effective/superseded status.
 * DRR-112: Backend dual-writes Policy primitive on import/supersede.
 * DRR-102: Mutations are scope-guarded on the backend.
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  SimpleGrid,
  Card,
  Badge,
  Table,
  Button,
  ActionIcon,
  Menu,
  Loader,
  Alert,
  Switch,
  TextInput,
  Modal,
  NumberInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconPlus,
  IconDots,
  IconEdit,
  IconArrowsExchange,
  IconSunset,
  IconAlertTriangle,
  IconShieldCheck,
  IconScale,
  IconSearch,
  IconAlertCircle,
} from '@tabler/icons-react';
import {
  useListRuleSetsQuery,
  useUpdateRuleSetMetadataMutation,
  useSunsetRuleSetMutation,
  type ListRuleSetsQuery,
} from '@/generated/graphql';
import { RuleSetImportModal } from './RuleSetImportModal';
import { RuleSetSupersedeModal } from './RuleSetSupersedeModal';

// ============================================================================
// TYPES
// ============================================================================

type RuleSetItem = NonNullable<ListRuleSetsQuery['ruleSets']>[number];

// ============================================================================
// HELPERS
// ============================================================================

function safetyColor(level: string): string {
  switch (level) {
    case 'critical': return 'red';
    case 'high': return 'orange';
    default: return 'gray';
  }
}

function effectivenessLabel(rs: RuleSetItem): { label: string; color: string } {
  if ('supersededBy' in rs && rs.supersededBy) return { label: 'Superseded', color: 'gray' };
  if ('isCurrentlyEffective' in rs && rs.isCurrentlyEffective === false) return { label: 'Pending', color: 'yellow' };
  if ('isCurrentlyEffective' in rs && rs.isCurrentlyEffective) return { label: 'Active', color: 'green' };
  return { label: 'Unknown', color: 'gray' };
}

// ============================================================================
// SUNSET CONFIRM MODAL
// ============================================================================

interface SunsetConfirmProps {
  opened: boolean;
  onClose: () => void;
  ruleSet: RuleSetItem | null;
}

function SunsetConfirmModal({ opened, onClose, ruleSet }: SunsetConfirmProps) {
  const [sunsetDate, setSunsetDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sunsetRuleSet, { loading }] = useSunsetRuleSetMutation();

  async function handleSunset() {
    if (!ruleSet || !sunsetDate) return;
    setError(null);
    try {
      await sunsetRuleSet({ variables: { rulesetId: ruleSet.rulesetId, sunsetDate } });
      setSunsetDate('');
      onClose();
    } catch (err) {
      setError(String(err));
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Set Sunset Date" size="sm">
      <Stack gap="sm">
        {ruleSet && (
          <Text size="sm">
            Setting a sunset date on <strong>{ruleSet.displayName}</strong> will mark it as
            inactive after that date. Historical queries can still retrieve it.
          </Text>
        )}
        <TextInput
          label="Sunset Date"
          placeholder="2026-12-31"
          description="ISO 8601 date — RuleSet stops being 'currently effective' after this date"
          required
          value={sunsetDate}
          onChange={(e) => setSunsetDate(e.target.value)}
        />
        {error && <Alert color="red" icon={<IconAlertCircle size={14} />}>{error}</Alert>}
      </Stack>
      <Group justify="space-between" mt="lg">
        <Button variant="subtle" onClick={onClose}>Cancel</Button>
        <Button color="orange" loading={loading} disabled={!sunsetDate} onClick={handleSunset}>
          Set Sunset Date
        </Button>
      </Group>
    </Modal>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export function RuleSetAdminPage() {
  const { data, loading, error, refetch } = useListRuleSetsQuery({
    variables: { effectiveOnly: false },
  });

  const rulesets = useMemo(() => (data?.ruleSets ?? []) as RuleSetItem[], [data]);

  const [search, setSearch] = useState('');
  const [hideSuperseded, setHideSuperseded] = useState(false);

  const [importOpened, { open: openImport, close: closeImport }] = useDisclosure(false);
  const [supersedeOpened, { open: openSupersede, close: closeSupersede }] = useDisclosure(false);
  const [sunsetOpened, { open: openSunset, close: closeSunset }] = useDisclosure(false);

  const [selectedRuleSet, setSelectedRuleSet] = useState<RuleSetItem | null>(null);

  const filtered = useMemo(() => {
    let items = rulesets;
    if (hideSuperseded) {
      items = items.filter((rs) => !('supersededBy' in rs && rs.supersededBy));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(
        (rs) =>
          rs.displayName.toLowerCase().includes(q) ||
          rs.rulesetId.toLowerCase().includes(q) ||
          rs.jurisdiction.toLowerCase().includes(q),
      );
    }
    return items;
  }, [rulesets, search, hideSuperseded]);

  const stats = useMemo(() => {
    const active = rulesets.filter((rs) => 'isCurrentlyEffective' in rs && rs.isCurrentlyEffective).length;
    const superseded = rulesets.filter((rs) => 'supersededBy' in rs && rs.supersededBy).length;
    const lifeCritical = rulesets.filter((rs) => rs.isLifeCritical).length;
    return { total: rulesets.length, active, superseded, lifeCritical };
  }, [rulesets]);

  function handleSupersede(rs: RuleSetItem) {
    setSelectedRuleSet(rs);
    openSupersede();
  }

  function handleSunset(rs: RuleSetItem) {
    setSelectedRuleSet(rs);
    openSunset();
  }

  function onMutationComplete() {
    refetch();
  }

  if (loading) return <Loader m="xl" />;
  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />} m="xl">
        Failed to load RuleSets: {error.message}
      </Alert>
    );
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Stack gap={2}>
          <Group gap="xs">
            <IconScale size={24} />
            <Title order={2}>RuleSet Administration</Title>
          </Group>
          <Text c="dimmed" size="sm">
            Import, supersede, and sunset legislation bundles. All changes dual-write Policy
            primitives (DRR-112) and are scope-guarded (regulatory:import).
          </Text>
        </Stack>
        <Button leftSection={<IconPlus size={16} />} onClick={openImport}>
          Import New RuleSet
        </Button>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={4} mb="lg">
        <Card withBorder p="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total RuleSets</Text>
          <Text size="xl" fw={700}>{stats.total}</Text>
        </Card>
        <Card withBorder p="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Currently Active</Text>
          <Text size="xl" fw={700} c="green">{stats.active}</Text>
        </Card>
        <Card withBorder p="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Superseded</Text>
          <Text size="xl" fw={700} c="gray">{stats.superseded}</Text>
        </Card>
        <Card withBorder p="md">
          <Group gap="xs">
            <IconShieldCheck size={16} color="red" />
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Life-Critical</Text>
          </Group>
          <Text size="xl" fw={700} c="red">{stats.lifeCritical}</Text>
        </Card>
      </SimpleGrid>

      {/* Filter bar */}
      <Group mb="md">
        <TextInput
          placeholder="Search by name, ID, or jurisdiction..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <Switch
          label="Hide superseded"
          checked={hideSuperseded}
          onChange={(e) => setHideSuperseded(e.target.checked)}
        />
      </Group>

      {/* Library Table */}
      <Paper withBorder>
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>Display Name</Table.Th>
              <Table.Th>Jurisdiction</Table.Th>
              <Table.Th>Safety</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Version</Table.Th>
              <Table.Th>Effective Date</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filtered.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text c="dimmed" size="sm" ta="center" py="md">No RuleSets found.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {filtered.map((rs) => {
              const eff = effectivenessLabel(rs);
              return (
                <Table.Tr key={rs.rulesetId} opacity={eff.color === 'gray' ? 0.6 : 1}>
                  <Table.Td>
                    <Text size="xs" ff="monospace" c="dimmed">{rs.rulesetId}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>{rs.displayName}</Text>
                    {rs.isLifeCritical && (
                      <Badge size="xs" color="red" variant="light" ml={4}>Life-Critical</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="outline">{rs.jurisdiction}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={safetyColor(rs.safetyLevel)}>{rs.safetyLevel}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" color={eff.color}>{eff.label}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{String(('version' in rs ? rs.version : undefined) ?? '—')}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{('effectiveDate' in rs ? rs.effectiveDate : null) ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu shadow="md" position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" size="sm">
                          <IconDots size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconArrowsExchange size={14} />}
                          onClick={() => handleSupersede(rs)}
                        >
                          Supersede (new version)
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconSunset size={14} />}
                          onClick={() => handleSunset(rs)}
                          color="orange"
                        >
                          Set Sunset Date
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Modals */}
      <RuleSetImportModal
        opened={importOpened}
        onClose={closeImport}
        onImported={onMutationComplete}
      />
      <RuleSetSupersedeModal
        opened={supersedeOpened}
        onClose={closeSupersede}
        onSuperseded={onMutationComplete}
        oldRuleSet={selectedRuleSet}
      />
      <SunsetConfirmModal
        opened={sunsetOpened}
        onClose={closeSunset}
        ruleSet={selectedRuleSet}
      />
    </Container>
  );
}
